import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { cfxMe, getCfxToken, setCfxToken, clearCfxToken, cfxLoginUrl } from "@/api";

const CfxAuthContext = createContext(null);

export const useCfxAuth = () => {
  const ctx = useContext(CfxAuthContext);
  if (!ctx) throw new Error("useCfxAuth must be inside CfxAuthProvider");
  return ctx;
};

export function CfxAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getCfxToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const u = await cfxMe();
      setUser(u);
    } catch (e) {
      clearCfxToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(() => {
    window.location.href = cfxLoginUrl();
  }, []);

  const logout = useCallback(() => {
    clearCfxToken();
    setUser(null);
  }, []);

  const ingestToken = useCallback(async (token) => {
    setCfxToken(token);
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, loading, login, logout, ingestToken, refresh }),
    [user, loading, login, logout, ingestToken, refresh]
  );

  return (
    <CfxAuthContext.Provider value={value}>
      {children}
    </CfxAuthContext.Provider>
  );
}
